import { Analytics, AnalyticsTracking } from 'appwrite';

import { shouldSkipAnalyticsPath } from '@/lib/analytics-context';
import { client } from '@/lib/appwrite-web';

// The property document ID. Shipping it to the browser is fine: ingestion is
// a public route, so the ID is not a secret.
const propertyId = (
    import.meta.env.VITE_APPWRITE_ANALYTICS_PROPERTY_ID ?? ''
).trim();

const analytics = new Analytics(client);

function pathnameOf(url: string) {
    try {
        return new URL(url, window.location.origin).pathname;
    } catch {
        return '';
    }
}

function emitEvent(
    name: string,
    options?: {
        url?: string;
        referrer?: string;
        propertyId?: string;
        scrollDepth?: number;
        engagementTime?: number;
        props?: Record<string, unknown>;
    },
) {
    if (typeof window === 'undefined') return;

    const url = options?.url ?? window.location.href;
    if (shouldSkipAnalyticsPath(pathnameOf(url))) return;

    const props = Object.entries(options?.props ?? {}).flatMap(([key, value]) =>
        value == null ? [] : [key, String(value)],
    );

    void analytics
        .createEvent({
            propertyId: options?.propertyId ?? propertyId,
            name,
            url,
            referrer: options?.referrer,
            scrollDepth: options?.scrollDepth,
            engagementTime: options?.engagementTime,
            props,
        })
        .catch(() => {
            // Tracking must never break the page.
        });
}

const tracking = propertyId
    ? new AnalyticsTracking(emitEvent, { propertyId })
    : null;

/** No-op until VITE_APPWRITE_ANALYTICS_PROPERTY_ID is set. */
export function startAppwriteAutoTracking(): () => void {
    if (!tracking || typeof window === 'undefined') return () => {};

    tracking.enableAllAutoTracking();
    tracking.enableAutoDownloadTracking();

    return () => {
        tracking.disableAutoPageviews();
        tracking.disableAutoOutboundTracking();
        tracking.disableAutoDownloadTracking();
        tracking.disableAutoScrollDepth();
        tracking.disableAutoEngagementTime();
    };
}

export function trackAppwriteEvent(
    name: string,
    props?: Record<string, unknown>,
    path?: string,
) {
    if (typeof window === 'undefined') return;

    // Router state updates before history.pushState, so location.href can still
    // be the previous page when this runs.
    const url = path ? new URL(path, window.location.origin).href : undefined;
    tracking?.track(name, { props, url });
}
